import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable } from 'rxjs';
import moment, * as moment_timezone from 'moment-timezone';
import { I18nService } from '@livemaps/i18n';
import { languageFallbackMap } from '@app/core/constants/language-fallback-map';
import { PermissionsService } from '@app/modules/auth';
import { VdoUser } from '@app/modules/auth/models/vdo-user';

export interface AppConfig {
  language: string;
  timeZone: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private config: AppConfig;
  private demoMode;

  constructor(private i18nService: I18nService, private permissionsService: PermissionsService) {
    // private auth0: AuthService
    const urlParams = new URLSearchParams(window.location.search);
    const demoMode = urlParams.get('demoMode')?.toLowerCase() ?? null;
    this.setDemoMode(demoMode);
    this.config = {
      language: this.getLocalLanguage(),
      timeZone: this.getLocalTimeZone()
    };
  }

  setDemoMode(mode: string) {
    this.demoMode = mode;
    if (mode === 'true') {
      sessionStorage.setItem('routeActivation', 'true');
      sessionStorage.setItem('demoMode', 'true');
    } else if (mode === 'false') {
      sessionStorage.removeItem('demoMode');
      sessionStorage.removeItem('routeActivation');
      sessionStorage.removeItem('assetVINsMap');
    }
  }

  loadConfig(): Observable<AppConfig> {
    return this.permissionsService.users$.pipe(
      map((users: VdoUser[]) => {
        if (users?.length) {
          let user = users[0];
          const language = user.locale || this.getLocalLanguage();
          const timeZone = this.getValidTimeZone(user.zoneinfo);
          this.setConfig({ language, timeZone });
        }
        return this.config;
      })
    );
  }

  setConfig(config: AppConfig) {
    this.config = config;
  }

  getConfig(): AppConfig {
    return this.config;
  }

  getUserLang() {
    const userLang = this.config.language;
    const resolvedLanguage = this.resolveLanguage(userLang);
    if (environment.i18n.supportedLanguages.includes(resolvedLanguage)) {
      return resolvedLanguage;
    }
    return this.getLocalLanguage();
  }

  getLocalTimeZone() {
    return moment_timezone.tz.guess();
  }

  getDemoMode() {
    return this.demoMode;
  }
  getValidTimeZone(tz) {
    return tz && tz?.trim() ? tz.trim() : this.getLocalTimeZone();
  }
  private getLocalLanguage() {
    // return browsers language as fallback
    let lang = this.getBrowserCultureLang();
    const resolvedLanguage = this.resolveLanguage(lang);
    lang = environment.i18n.supportedLanguages.includes(resolvedLanguage)
      ? resolvedLanguage
      : environment.i18n.defaultLanguage;
    return lang;
  }

  getBrowserCultureLang(): string {
    if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
      return undefined;
    }

    let browserCultureLang: any = window.navigator.languages ? window.navigator.languages[0] : null;
    browserCultureLang =
      browserCultureLang ||
      window.navigator.language ||
      // tslint:disable-next-line:no-string-literal
      window.navigator['browserLanguage'] ||
      window.navigator['userLanguage'];

    return browserCultureLang;
  }
  resolveLanguage(lang: string) {
    return this.i18nService.getLanguageInUse(environment.i18n.supportedLanguages, lang, languageFallbackMap, false);
  }
}
